import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

// Line-item editor for offline orders: category -> product filtering (both searchable),
// live totals, and a manually editable order total that defaults to the sum of the items.
export default class extends Controller {
  static targets = ["rows", "template", "itemsTotal", "discountInput", "discountDisplay", "totalInput", "resetButton"]
  static values = { products: Array, manualTotal: Boolean }

  connect() {
    this.rowsTarget.querySelectorAll("[data-row]").forEach((row) => this.initRow(row))
    this.recalculate()
  }

  add() {
    const html = this.templateTarget.innerHTML.replaceAll("NEW_RECORD", Date.now().toString())
    this.rowsTarget.insertAdjacentHTML("beforeend", html)
    this.initRow(this.rowsTarget.lastElementChild)
    this.recalculate()
  }

  remove(event) {
    const row = event.currentTarget.closest("[data-row]")
    const destroy = row.querySelector("[data-destroy]")
    if (destroy) {
      destroy.value = "1"
      row.classList.add("hidden")
    } else {
      row.remove()
    }
    this.recalculate()
  }

  categoryChanged(event) {
    const row = event.currentTarget.closest("[data-row]")
    this.fillProducts(row, event.currentTarget.value, null)
    row.querySelector("[data-price]").value = ""
    this.recalculate()
  }

  productChanged(event) {
    const row = event.currentTarget.closest("[data-row]")
    const product = this.productsValue.find((p) => p.id === event.currentTarget.value)
    row.querySelector("[data-price]").value = product ? product.price : ""
    this.recalculate()
  }

  totalEdited() {
    this.manualTotalValue = true
    this.updateReset()
  }

  resetTotal() {
    this.manualTotalValue = false
    this.recalculate()
  }

  recalculate() {
    let sum = 0
    this.rowsTarget.querySelectorAll("[data-row]:not(.hidden)").forEach((row) => {
      const qty = parseFloat(row.querySelector("[data-quantity]").value) || 0
      const price = parseFloat(row.querySelector("[data-price]").value) || 0
      const line = this.round(qty * price)
      row.querySelector("[data-line-total]").textContent = `₹${line.toFixed(2)}`
      sum += line
    })
    sum = this.round(sum)
    const discount = this.round(parseFloat(this.discountInputTarget.value) || 0)
    const payable = Math.max(this.round(sum - discount), 0)
    this.itemsTotalTarget.textContent = `₹${sum.toFixed(2)}`
    this.discountDisplayTarget.textContent = `₹${discount.toFixed(2)}`
    if (!this.manualTotalValue) this.totalInputTarget.value = payable.toFixed(2)
    this.updateReset()
  }

  // -- helpers --

  initRow(row) {
    const productSelect = row.querySelector("[data-product]")
    const selected = productSelect.dataset.selected || productSelect.value
    const product = this.productsValue.find((p) => p.id === selected)
    const categorySelect = row.querySelector("[data-category]")

    const searchable = { maxOptions: null, selectOnTab: true, plugins: ["dropdown_input"] }
    const categoryTs = new TomSelect(categorySelect, { ...searchable, placeholder: "Select category" })
    new TomSelect(productSelect, { ...searchable, placeholder: "Select category first" })

    if (product) categoryTs.setValue(product.categoryId, true)
    this.fillProducts(row, categorySelect.value, selected)
  }

  fillProducts(row, categoryId, selectedId) {
    const ts = row.querySelector("[data-product]").tomselect
    ts.clear(true)
    ts.clearOptions()
    ts.addOptions(
      this.productsValue
        .filter((p) => p.categoryId === categoryId)
        .map((p) => ({ value: p.id, text: `${p.name} (₹${p.price}/${p.unit}) · ${p.stock > 0 ? p.stock + " in stock" : "out of stock"}` }))
    )
    ts.clear(true) // don't let the first option get auto-selected
    // With the dropdown_input plugin the control's empty-state text lives in .items-placeholder
    ts.settings.placeholder = categoryId ? "Select product" : "Select category first"
    const controlPlaceholder = ts.control.querySelector(".items-placeholder")
    if (controlPlaceholder) controlPlaceholder.placeholder = ts.settings.placeholder
    ts.control_input.setAttribute("placeholder", "Type to search")
    if (selectedId) ts.setValue(selectedId, true)
    ts.refreshOptions(false)
  }

  updateReset() {
    this.resetButtonTarget.classList.toggle("hidden", !this.manualTotalValue)
  }

  round(value) {
    return Math.round(value * 100) / 100
  }
}
